import { useState, useEffect } from "react";
import { X, Mail, ArrowRight, CheckCircle, Sparkles } from "lucide-react";

const STORAGE_KEY = "fanible_beta_signup_done";

export default function BetaSignupModal({ open: externalOpen = false, onClose }) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [alreadyExists, setAlreadyExists] = useState(false);

  // Auto-show on first visit (if not already submitted)
  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEY)) return;
    const t = setTimeout(() => setInternalOpen(true), 1200);
    return () => clearTimeout(t);
  }, []);

  const visible = externalOpen || internalOpen;

  const dismiss = () => {
    setInternalOpen(false);
    if (onClose) onClose();
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim()) return setError("Please enter your Gmail address.");
    if (!email.toLowerCase().endsWith("@gmail.com"))
      return setError("Only @gmail.com addresses are accepted for Play Store pre-registration.");

    setLoading(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API}/api/beta/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Something went wrong.");
      setSuccess(true);
      setAlreadyExists(data.already_exists);
      localStorage.setItem(STORAGE_KEY, "1");
      setTimeout(() => {
        setInternalOpen(false);
        if (onClose) onClose();
      }, 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!visible) return null;

  return (
    <div
      data-testid="beta-signup-overlay"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(8px)" }}
    >
      <div
        data-testid="beta-signup-modal"
        className="relative w-full max-w-md rounded-3xl overflow-hidden"
        style={{
          background: "linear-gradient(145deg, #111420 0%, #0C0E13 100%)",
          border: "1px solid rgba(59,158,255,0.15)",
          boxShadow: "0 0 80px rgba(59,158,255,0.10), 0 32px 64px rgba(0,0,0,0.6)",
        }}
      >
        {/* Glow accent top */}
        <div
          className="absolute top-0 left-0 right-0 h-px"
          style={{
            background:
              "linear-gradient(90deg, transparent, rgba(59,158,255,0.7) 40%, rgba(96,189,255,1) 50%, rgba(59,158,255,0.7) 60%, transparent)",
          }}
        />
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-40 pointer-events-none"
          style={{
            background: "radial-gradient(ellipse at top, rgba(59,158,255,0.12) 0%, transparent 70%)",
          }}
        />

        {/* Close */}
        <button
          data-testid="beta-signup-close"
          onClick={dismiss}
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors duration-150 z-10"
        >
          <X size={14} className="text-white/50" />
        </button>

        <div className="px-8 pt-10 pb-8">
          {!success ? (
            <>
              {/* Header */}
              <div className="mb-6">
                <div className="inline-flex items-center gap-2 bg-[#3B9EFF]/10 border border-[#3B9EFF]/25 rounded-full px-3 py-1.5 mb-5">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#3B9EFF] opacity-70" />
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#3B9EFF]" />
                  </span>
                  <span className="text-[10px] font-black uppercase tracking-[0.2em] text-[#3B9EFF]">
                    Pre-Registration · Now Open
                  </span>
                </div>

                <h2 className="font-outfit font-black text-white text-2xl sm:text-3xl tracking-tight leading-tight mb-2">
                  Pre-Register for<br />
                  <span className="text-[#3B9EFF]">Fanible</span>
                </h2>
                <p className="text-sm text-[#A1A1AA] font-inter leading-relaxed">
                  Pre-registration is live on Google Play Store. Drop your Gmail below and we'll notify you the moment Fanible launches.
                </p>
              </div>

              {/* Form */}
              <form onSubmit={submit} className="space-y-3">
                <div className="relative">
                  <Mail
                    size={15}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A1A1AA] pointer-events-none"
                  />
                  <input
                    data-testid="beta-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setError(""); }}
                    placeholder="yourname@gmail.com"
                    className="w-full bg-white/[0.05] border border-white/10 rounded-xl pl-10 pr-4 py-3.5 text-white text-sm font-inter placeholder-[#555] outline-none focus:border-[#3B9EFF]/50 focus:bg-white/[0.07] transition-all duration-150"
                  />
                </div>

                {error && (
                  <p data-testid="beta-email-error" className="text-red-400 text-xs font-inter px-1">
                    {error}
                  </p>
                )}

                <button
                  data-testid="beta-signup-submit"
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 bg-[#3B9EFF] text-black font-bold text-sm uppercase tracking-wider rounded-xl py-4 hover:bg-white transition-colors duration-150 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Submitting...
                    </span>
                  ) : (
                    <>
                      Notify Me at Launch <ArrowRight size={15} />
                    </>
                  )}
                </button>
              </form>

              <p className="text-[10px] text-[#555] text-center mt-4 font-inter leading-relaxed">
                Gmail links your Google Play pre-registration.
                <br />We'll never spam or share your email.
              </p>
            </>
          ) : (
            /* Success State */
            <div data-testid="beta-signup-success" className="text-center py-4">
              <div className="w-16 h-16 rounded-full bg-[#3B9EFF]/10 border border-[#3B9EFF]/25 flex items-center justify-center mx-auto mb-6">
                <CheckCircle size={32} className="text-[#3B9EFF]" />
              </div>
              <h3 className="font-outfit font-black text-white text-xl mb-2">
                {alreadyExists ? "Already Pre-Registered!" : "You're Pre-Registered!"}
              </h3>
              <p className="text-sm text-[#A1A1AA] font-inter leading-relaxed">
                {alreadyExists
                  ? "This Gmail is already registered. We'll notify you the moment Fanible launches."
                  : "Your Gmail has been submitted. We'll notify you the moment Fanible launches on Google Play Store — keep an eye on your inbox!"}
              </p>
              <div className="flex items-center justify-center gap-1.5 mt-5">
                <Sparkles size={12} className="text-[#3B9EFF]" />
                <span className="text-[10px] font-bold uppercase tracking-widest text-[#3B9EFF]">
                  Fanible · Pre-Registration Open
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
